import { Home, Search, Plus, MessageCircle, User, Heart, Package, HelpCircle, Calendar, TrendingUp } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'
import { POST_CATEGORIES } from '@/constants'

const navItems = [
  { icon: Home, label: '首页', path: '/', color: 'text-blue-500' },
  { icon: TrendingUp, label: '热门', path: '/trending', color: 'text-red-500' },
  { icon: Heart, label: '学习', path: `/category/${encodeURIComponent('学习')}`, color: 'text-pink-500' },
  { icon: Package, label: '生活', path: `/category/${encodeURIComponent('生活')}`, color: 'text-green-500' },
  { icon: Search, label: '娱乐', path: `/category/${encodeURIComponent('娱乐')}`, color: 'text-purple-500' },
  { icon: HelpCircle, label: '求助', path: `/category/${encodeURIComponent('求助')}`, color: 'text-yellow-600' },
  { icon: Calendar, label: '交易', path: `/category/${encodeURIComponent('交易')}`, color: 'text-indigo-500' },
  { icon: Calendar, label: '其他', path: `/category/${encodeURIComponent('其他')}`, color: 'text-gray-500' },
]

const quickActions = [
  { icon: Plus, label: '发布动态', path: '/publish', primary: true },
  { icon: MessageCircle, label: '我的消息', path: '/messages' },
  { icon: User, label: '个人中心', path: '/profile' },
]

export function SideNavigation() {
  const location = useLocation()
  const { user } = useAuthStore()

  return (
    <div className="space-y-6">
      {/* 主导航 */}
      <div className="bg-white/50 rounded-lg p-4 border border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          分类导航
        </h3>
        <nav className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.path
            
            return (
              <Link key={item.path} to={item.path}>
                <div className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                  isActive 
                    ? "bg-orange-50 text-orange-600 border-r-2 border-orange-500" 
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                )}>
                  <Icon className={cn("w-5 h-5", isActive ? "text-orange-500" : item.color)} />
                  <span>{item.label}</span>
                </div>
              </Link>
            )
          })}
        </nav>
      </div>

      {/* 快捷操作 */}
      <div className="bg-white/50 rounded-lg p-4 border border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
          快捷操作
        </h3>
        <div className="space-y-2">
          {quickActions.map((action) => {
            const Icon = action.icon
            const isActive = location.pathname === action.path
            
            if (action.primary) {
              return (
                <Link key={action.path} to={action.path}>
                  <Button className="w-full justify-start bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-md hover:shadow-lg transition-all duration-200">
                    <Icon className="w-4 h-4 mr-2" />
                    {action.label}
                  </Button>
                </Link>
              )
            }
            
            return (
              <Link key={action.path} to={action.path}>
                <Button 
                  variant={isActive ? "default" : "ghost"} 
                  className={cn(
                    "w-full justify-start transition-all duration-200",
                    isActive 
                      ? "bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100" 
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-transparent hover:border-gray-200"
                  )}
                >
                  <Icon className={cn(
                    "w-4 h-4 mr-2",
                    isActive ? "text-blue-500" : "text-gray-500"
                  )} />
                  {action.label}
                </Button>
              </Link>
            )
          })}
        </div>
      </div>

      {/* 用户信息（真实数据） */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-100">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
          我的账号
        </h4>
        {user ? (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">昵称</span>
              <span className="font-medium text-blue-600">{user.nickname || user.username}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">角色</span>
              <span className="font-medium text-orange-500">{user.role === 'admin' ? '管理员' : '普通用户'}</span>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500 space-y-2">
            <p>登录后查看账号信息</p>
            <Link to="/login">
              <Button size="sm" className="w-full">去登录</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}